import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import { Component, inject } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterOutlet } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import type { Observable } from 'rxjs';
import { combineLatest, map } from 'rxjs';

import type { AppIdentity } from '@evc/platform';
import { NavigationsService, permissions, PlatformComponent, PlatformService } from '@evc/platform';
import type { Menu, MenuItemType } from '@evc/web-components';
import {
  ButtonComponent,
  CloseOnClickOutsideDirective,
  IconComponent,
  ModalService,
} from '@evc/web-components';
import { SharedConfigService } from '@shared/services/config/config.service';
import { I18nService } from '@shared/services/i18n/i18n.service';

import { ContainerComponent } from '@app/layout/container/container.component';
import { NavigationConfigService } from '@app/services/navigation-config/navigation-config.service';
import { loadPersistantCellar } from '@app/store/actions/cellar/cellar.action';
import { uploadFiles } from '@app/store/actions/upload/upload.action';
import { getRouterState, isListEmpty, isUploading } from '@app/store/selectors';
import { DriveConfigService } from '@app/types/config.types';
import { config } from '../environments/config';
import { AddButtonMenuComponent } from './components';
import { createFolder, deleteAllTrash } from './store/actions/file/file.action';

export type MainActionHost = 'desktop' | 'mobile';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    NgTemplateOutlet,
    RouterOutlet,
    MatProgressSpinnerModule,
    IconComponent,
    PlatformComponent,
    ButtonComponent,
    AddButtonMenuComponent,
    ContainerComponent,
    CloseOnClickOutsideDirective,
    TranslatePipe,
    AsyncPipe,
  ],
})
export class AppComponent implements OnInit {
  readonly i18nService = inject<I18nService>(I18nService);
  readonly modalService = inject<ModalService>(ModalService);
  readonly #configService: DriveConfigService = inject(SharedConfigService);
  readonly #platformService = inject(PlatformService);
  readonly #navigationService = inject(NavigationConfigService);
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly #navigationsService = inject(NavigationsService);

  protected PERMISSIONS = permissions;

  uploading$: Observable<boolean> = this.#store.pipe(select(isUploading));
  emptyList$: Observable<boolean> = this.#store.pipe(select(isListEmpty));

  routerLoading$: Observable<boolean> = this.#store.pipe(
    select(getRouterState),
    map((routerState) => !routerState),
  );

  isSpinnerVisible$: Observable<boolean> = combineLatest([
    this.routerLoading$,
    this.uploading$,
  ]).pipe(map(([routerLoading, uploading]) => routerLoading || uploading));

  appSettingMenu: MenuItemType[];
  leftBarConfig: Signal<Menu[]>;
  appHelpMenu: Signal<MenuItemType[]>;
  appIdentification: Signal<AppIdentity>;
  isLeftBarOpen = this.#navigationsService.isLeftbarOpen.bind(this.#navigationsService);
  isAddMenuOpenFrom: MainActionHost | undefined = undefined;
  appSearch: (event: any) => void;

  readonly ready = this.#platformService.ready;

  constructor() {
    this.#configService.provideConfig(config);
  }

  ngOnInit(): void {
    this.#store.dispatch(loadPersistantCellar());
    this.initNavigation();
  }

  uploadFiles({ files, paths }: { files: File[]; paths?: string[] }): void {
    this.#store.dispatch(uploadFiles({ files, paths }));
    this.hideAddMenu();
  }

  createFolder(): void {
    this.#store.dispatch(createFolder());
  }

  isTrashMenu(): boolean {
    return this.#router.url.includes('trash');
  }
  ctaButtonClicked(from: MainActionHost): void {
    if (this.isTrashMenu()) {
      this.#store.dispatch(deleteAllTrash());
    } else {
      this.isAddMenuOpenFrom = from;
    }
  }

  hideAddMenu(): void {
    this.isAddMenuOpenFrom = undefined;
  }

  private initNavigation(): void {
    this.appSettingMenu = this.#navigationService.appSettingMenu;
    this.leftBarConfig = this.#navigationService.leftbarConfig;
    this.appIdentification = this.#navigationService.appIdentity;
    this.appHelpMenu = this.#navigationService.helpMenu;
    this.appSearch = this.#navigationService.appSearch;
  }
}
